
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../i18n";
import { Schemes, Utils } from "../../../utils";
import CheersSvg from "../../../assets/images/cheers.svg";
import { ConfirmDialog } from "../../shared/ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { AlertInterface, Button, FormControl, ObjectHelper, Scheme, TextInput, Form, NumberInput, DateTimeInput, Checkbox, Alignment, Dropdown, DOMHelper, Subscriber, NoseurObject, StringHelper } from "@rivtn/noseur";
import { ApiResponse, BaseService, ChargesFeeService, IChargeFee, IMember, ITournamentEditionRegistered, ITeam, ITeamMember, ITournamentEdition, ITournamentEditionCustomField, RegisteredTournamentStatus, SanitizedResponse, TeamService, TeamTypes, TeamTypesValue, TournamentCustomFieldType, TournamentService } from "../../../services";
import PageDesign from "../../page_design/PageDesign";
import { Elements } from "../../shared/Elements";

let dialog: AlertInterface;
const teamService = TeamService.getInstance();
const tournamentService = TournamentService.getInstance();
const chargesFeeService = ChargesFeeService.getInstance();
const labels = i18nManager.Labels.tournaments.dialog.register;


export function RegisterForTournament(profile: IMember, registeredTournament: ITournamentEditionRegistered, state?: string) {
    const width = document.documentElement.clientWidth;
    const mutableRoot = { current: null } as unknown as React.RefObject<ReactDOM.Root>;
    const controlMutableRoot = { current: null } as unknown as React.RefObject<ReactDOM.Root>;
    const secondaryMutableRoot = { current: null } as unknown as React.RefObject<ReactDOM.Root>;

    dialog = ConfirmDialog({
        noControl: true,
        contentStyle: { padding: 0 },
        style: width > 800 ? { maxWidth: "90%" } : { minHeight: "100%", minWidth: "100%" },
        content: (<div className="tournament-reg-cont">
            <div className="tournament-register">
                {buildJoinTournamentHeader(profile, registeredTournament)}
                <div className="content" ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(mutableRoot, ReactDOM.createRoot(r));
                }}>
                    <img alt={"loading tournament hosts"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
                </div>
                <div className="controls" ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(controlMutableRoot, ReactDOM.createRoot(r));
                }} />
            </div>
            <div className="tournament-register-secondary" ref={(r: any) => {
                if (!r) return;
                ObjectHelper.resolveRef(secondaryMutableRoot, ReactDOM.createRoot(r));
            }} />
        </div>),
    });
    dialog.show(() => {
        validateUserCanRegisterForTournament(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, state);
    });
}

function validateUserCanRegisterForTournament(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>, state?: string) {
    const tournamentEdition = registeredTournament.tournament_edition;

    function onPreValidateRegistration() {
        if (state === "SHOW_RULE") {
            showTournamentRule(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            return;
        } else if (state === "SHOW_WARNING") {
            showWarning(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            return;
        } else if (state === "SHOW_REQUIRED_FIELDS") {
            showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            return;
        } else if (state === "TEAM_SELECTION_AND_PAYMENT") {
            showTeamSelectionAndPaymentDetails(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            return;
        }
        showImportantInformation(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
    }

    if (registeredTournament.status === RegisteredTournamentStatus.REGISTERED) {
        onPreValidateRegistration();
        return;
    }

    tournamentService.validateIfUserCanRegister(tournamentEdition.external_id).then(({ apiResponse }: SanitizedResponse<ApiResponse<any>>) => {
        if (apiResponse && !apiResponse.success) {
            mutableRoot.current?.render(buildMainContentView({ text: labels.you_shall_not_pass, icon: "fa fa-times err" }, apiResponse.message));
            controlMutableRoot.current?.render(buildControlsView({ scheme: Scheme.DANGER, text: i18nManager.Labels.common.close, onClick: () => dialog?.hide() }));
            return;
        }
        onPreValidateRegistration();
    }).catch((err) => {
        BaseService.reportError(err);
        mutableRoot.current?.render(buildMainContentView({ text: labels.you_shall_not_pass, icon: "fa fa-times err" }, err.message));
        controlMutableRoot.current?.render(buildControlsView({ scheme: Scheme.DANGER, text: i18nManager.Labels.common.close, onClick: () => dialog?.hide() }));
    });
}

export function buildJoinTournamentHeader(profile: IMember, registeredTournament: ITournamentEditionRegistered, hideCloseIcon?: boolean) {
    const tournamentEdition = registeredTournament.tournament_edition;
    const profileCurrency = i18nManager.getCountryCurrency(profile.country_code);
    const tournamentPrizeCurrency = ((tournamentEdition.meta?.price_pool_amounts && profileCurrency in tournamentEdition.meta?.price_pool_amounts) ? profileCurrency : Object.keys(tournamentEdition.meta?.price_pool_amounts ?? {})[0]);

    return (<>
        <div className="bn">
            <img alt={tournamentEdition.name} src={tournamentEdition.banner_url} />
            {!hideCloseIcon && (<i className="fa fa-times close" onClick={() => dialog?.hide(() => Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { clear: true }))} />)}
            <span className="name">{tournamentEdition.name}</span>
            <div>
                <div>
                    {(tournamentEdition.meta?.price_pool_monetary || tournamentEdition.meta?.price_pool_physical) ?
                        (<>{tournamentEdition.meta?.price_pool_physical && (<i className="fa fa-gift" />)}
                            <span>{(tournamentEdition.meta?.price_pool_monetary && tournamentEdition.meta?.price_pool_amounts) && (<>
                                {i18nManager.getCurrencySign(tournamentPrizeCurrency)} {StringHelper.toPresentableMoneyValue(tournamentEdition.meta?.price_pool_amounts?.[tournamentPrizeCurrency] ?? 0)}
                            </>)}</span>
                        </>) :
                        (<div className="no-prize"></div>)}
                </div>
                <div>
                    <i className="fa fa-users" />
                    <span>{i18nManager.Labels.teams.team_type_presentable[tournamentEdition.team_type.toLowerCase()]}</span>
                </div>
                <div>
                    <i className="fa fa-cog" />
                    <span>{tournamentEdition.program.name}</span>
                </div>
            </div>
        </div>
        <img className="logo" alt={tournamentEdition.name} src={tournamentEdition?.logo_url} />
    </>);
}

function renderLoadingView(mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>) {
    mutableRoot.current?.render(<img alt={"loading tournament hosts"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />);
    controlMutableRoot.current?.render(undefined);
}

function buildMainContentView(header: { text: string; icon: string; style?: React.CSSProperties; }, message: React.ReactElement | string) {
    return (<>
        <div>
            {header && (<>
                <i className={header.icon} style={header.style} />
                <span>{header.text}</span>
            </>)}
        </div>
        {message}
    </>);
}

function buildControlsView(action: { scheme: any; text: string; disabled?: boolean; onClick: () => void; }, cancellable?: boolean | string | { text: string; disabled?: boolean; onClick: () => void; }) {
    return (<>
        {cancellable && (<Button textOnly scheme={Schemes.OMNEHOST} disabled={typeof cancellable === "object" ? cancellable.disabled : undefined}
            onClick={() => {
                if (typeof cancellable === "object") {
                    cancellable.onClick();
                } else {
                    dialog?.hide(() => Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { clear: true }));
                }
            }}
            text={cancellable === true ? i18nManager.Labels.common.close : (typeof cancellable === "string" ? cancellable : cancellable.text)} />)}
        <Button raised scheme={action.scheme} text={action.text} disabled={action.disabled} onClick={action.onClick} />
    </>);
}

function showImportantInformation(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>) {
    const tournamentEdition = registeredTournament.tournament_edition;
    mutableRoot.current?.render(buildMainContentView({ text: labels.important_info, icon: "fa fa-info info", style: { padding: "3px 6px", fontSize: 10 } }, (<>
        <span>{labels.refund_policy.info} <a target="_blank" href={labels.refund_policy.article_link}>{labels.refund_policy.article_label}</a></span>
        <ul style={{ overflow: "auto", marginBottom: 0 }}>
            {(Object.entries(tournamentEdition.participants_settings).map(([key, value]) => {
                if (typeof value != "boolean" || !value) return;
                const label = ((labels.participant_requirements as any)[key] ?? key)
                    .replaceAll("${tournament_host_name}", tournamentEdition.tournament.host.name)
                    .replaceAll("${rated}", tournamentEdition.participants_settings.top_xin_program)
                    .replaceAll("${tournament_host_link}", Utils.buildPublicUrl("HOST", tournamentEdition.tournament.host.public_id));
                return (<li key={key} dangerouslySetInnerHTML={{ __html: label }} />);
            }))}
        </ul>
    </>)));
    controlMutableRoot.current?.render(buildControlsView({
        scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.continue, onClick: () => {
            if (tournamentEdition.rule) {
                showTournamentRule(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                return;
            }
            showWarning(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
        }
    }, true));
}

function showTournamentRule(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>) {
    const tournamentEdition = registeredTournament.tournament_edition;
    secondaryMutableRoot.current?.render(<div className="tour-rule">
        <span className="t">{labels.tournament_rule}</span>
        <p style={{ marginTop: 10, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: tournamentEdition.rule?.replaceAll("\n\n", "<br /><br />")! }} />
    </div>);
    Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { id: tournamentEdition.external_id, state: "SHOW_RULE" });
    controlMutableRoot.current?.render(buildControlsView({
        scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.continue, onClick: () => {
            showWarning(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
        }
    }, true));
}

function showWarning(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>, directToPayment?: boolean) {
    const tournamentEdition = registeredTournament.tournament_edition;
    const profileCountry = i18nManager.getCountryName(profile.country_code);
    const profileCurrency = i18nManager.getCountryCurrency(profile.country_code);
    const tournamentCountry = i18nManager.getCountryName(tournamentEdition.country_code);
    const profileCurrencyName = i18nManager.getCountryCurrencyName(profile.country_code);
    const supportedPrizeCurrencies = Object.keys(tournamentEdition.meta?.price_pool_amounts ?? {}).map((curr) => `${i18nManager.getCurrencyName(curr)} - ${curr}`).join(", ");
    const monetaryPrizeNotInCountry = tournamentEdition.meta.price_pool_monetary && !(tournamentEdition.meta?.price_pool_amounts && profileCurrency in tournamentEdition.meta?.price_pool_amounts);
    const physicalPrizeNotInCountry = tournamentEdition.meta?.price_pool_physical && !(tournamentEdition.meta?.price_pool_physical_prize_available_in_all_countries || tournamentEdition.country_code === profile.country_code);

    if (!directToPayment) secondaryMutableRoot.current?.render(null);
    Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { id: tournamentEdition.external_id, state: "SHOW_WARNING" });
    mutableRoot.current?.render(buildMainContentView({ text: labels.something_to_note, icon: "fa fa-warning warning", style: { padding: "3px 3px", fontSize: 10 } }, (<>
        {monetaryPrizeNotInCountry && (<span style={{ marginTop: 10 }}>{labels.prize_info.monetary_prize_not_in_currency
            .replaceAll("${currency}", `${profileCurrencyName} - ${profileCurrency}`)
            .replaceAll("${currencies}", supportedPrizeCurrencies)}</span>)}
        {physicalPrizeNotInCountry && (<span style={{ marginTop: 10 }}>{labels.prize_info.physical_prize_not_in_country
            .replaceAll("${country}", profileCountry)
            .replaceAll("${tournament_country}", tournamentCountry)}</span>)}
        <a style={{ marginTop: 10 }} href={labels.prize_info.claiming_monetary_prize.location} target="_blank">{labels.prize_info.claiming_monetary_prize.label}</a>
        <a style={{ marginTop: 5 }} href={labels.prize_info.claiming_physical_prize.location} target="_blank">{labels.prize_info.claiming_physical_prize.label}</a>
    </>)));
    if (!directToPayment) {
        controlMutableRoot.current?.render(buildControlsView({
            scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.continue, onClick: () => {
                showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            }
        }, {
            text: i18nManager.Labels.common.back,
            onClick: () => {
                showImportantInformation(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                if (tournamentEdition.rule) {
                    showTournamentRule(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                }
            }
        }));
    }
}

function showRequiredInformations(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>) {
    renderLoadingView(mutableRoot, controlMutableRoot);
    const tournamentEdition = registeredTournament.tournament_edition;
    tournamentService.getTournamentEditionCustomFields(tournamentEdition.external_id).then(({ apiResponse }: SanitizedResponse<ITournamentEditionCustomField>) => {
        if (!apiResponse.success || !apiResponse.data?.content?.length) {
            completeFormAndStartRegistration(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            return;
        }
        showTournamentRequiredForm(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, apiResponse.data?.content);
    }).catch((err) => {
        BaseService.reportError(err);
        mutableRoot.current?.render(buildMainContentView({ text: labels.you_shall_not_pass, icon: "fa fa-times err" }, err.message));
        controlMutableRoot.current?.render(buildControlsView({ scheme: Scheme.DANGER, text: i18nManager.Labels.common.close, onClick: () => dialog?.hide() }));
    });
}

function showTournamentRequiredForm(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>, form: ITournamentEditionCustomField[]) {
    const tournamentEdition = registeredTournament.tournament_edition;
    const requiredFields = form.filter((f) => f.required).map((f) => f.external_id);
    const textInputs = form.filter((f) => f.type === TournamentCustomFieldType.TEXT);
    const dateInputs = form.filter((f) => f.type === TournamentCustomFieldType.DATE);
    const checkInputs = form.filter((f) => f.type === TournamentCustomFieldType.CHECK);
    const numberInputs = form.filter((f) => f.type === TournamentCustomFieldType.NUMBER);
    const optionsInputs = form.filter((f) => f.type === TournamentCustomFieldType.OPTIONS);
    if (!registeredTournament.custom_fields_answers) registeredTournament.custom_fields_answers = {};

    showWarning(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, true);
    Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { id: tournamentEdition.external_id, state: "SHOW_REQUIRED_FIELDS" });
    secondaryMutableRoot.current?.render(<>
        <span style={{ fontWeight: "bold", fontSize: 18, margin: 15 }}>{labels.required_info}</span>
        <Form className="tour-form" scheme={Schemes.OMNEHOST}>
            {textInputs.map((input) => {
                return (<FormControl fill key={input.external_id} label={<span style={{ color: "color: var(--textColor)" }}>{input.label} {input.required && (<span style={{ color: "#FF7171" }}>*</span>)}</span>} style={{ marginTop: 15 }}>
                    <TextInput fill scheme={Schemes.OMNEHOST} defaultValue={registeredTournament.custom_fields_answers?.[input.external_id]} onInputComplete={(v) => {
                        registeredTournament.custom_fields_answers[input.external_id] = v;
                    }} />
                </FormControl>);
            })}
            {textInputs.length && (<hr style={{ marginTop: 30 }} />)}

            {numberInputs.map((input) => {
                return (<FormControl fill key={input.external_id} label={<span style={{ color: "color: var(--textColor)" }}>{input.label} {input.required && (<span style={{ color: "#FF7171" }}>*</span>)}</span>} style={{ marginTop: 15 }}>
                    <NumberInput fill scheme={Schemes.OMNEHOST} defaultValue={registeredTournament.custom_fields_answers?.[input.external_id]} onInputComplete={(v) => {
                        registeredTournament.custom_fields_answers[input.external_id] = parseInt(v);
                    }} />
                </FormControl>);
            })}
            {numberInputs.length && (<hr style={{ marginTop: 30 }} />)}

            {dateInputs.map((input) => {
                return (<FormControl fill key={input.external_id} label={<span style={{ color: "color: var(--textColor)" }}>{input.label} {input.required && (<span style={{ color: "#FF7171" }}>*</span>)}</span>} style={{ marginTop: 15 }} leftContent={"fa fa-calendar"}>
                    <DateTimeInput fill scheme={Schemes.OMNEHOST} selectedDates={registeredTournament.custom_fields_answers?.[input.external_id] ? [new Date(registeredTournament.custom_fields_answers[input.external_id])] : undefined} onSelectDate={(v) => {
                        registeredTournament.custom_fields_answers[input.external_id] = v.formatDate(v.selectedDate!);
                    }} />
                </FormControl>);
            })}
            {dateInputs.length && (<hr style={{ marginTop: 30 }} />)}

            {optionsInputs.map((input) => {
                return (<div key={input.external_id} style={{ marginTop: 20 }}>
                    <span style={{ color: "color: var(--textColor)" }}>{input.label} {input.required && (<span style={{ color: "#FF7171" }}>*</span>)}</span>
                    {input.options?.map((option) => {
                        return (<Checkbox style={{ marginTop: 10 }} key={option} label={option} defaultChecked={registeredTournament.custom_fields_answers?.[input.external_id] === option} scheme={Schemes.OMNEHOST} onChecked={(v) => {
                            if (v) registeredTournament.custom_fields_answers[input.external_id] = option;
                        }} />);
                    })}
                </div>);
            })}
            {optionsInputs.length && (<hr style={{ marginTop: 30 }} />)}

            {checkInputs.map((input) => {
                return (<Checkbox style={{ marginTop: 15 }} key={input.external_id} defaultChecked={registeredTournament.custom_fields_answers?.[input.external_id]}
                    label={<span style={{ color: "color: var(--textColor)" }}>{input.label} {input.required && (<span style={{ color: "#FF7171" }}>*</span>)}</span>} />);
            })}

        </Form>
    </>);
    controlMutableRoot.current?.render(buildControlsView({
        scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.continue, onClick: () => {
            for (const requiredField of requiredFields) {
                if (!(requiredField in registeredTournament.custom_fields_answers) || !registeredTournament.custom_fields_answers?.[requiredField]) {
                    BaseService.reportError(labels.required_fields_missing as any);
                    return;
                }
            }
            completeFormAndStartRegistration(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
        }
    }, {
        text: i18nManager.Labels.common.back,
        onClick: () => {
            secondaryMutableRoot.current?.render(null);
            showImportantInformation(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            if (tournamentEdition.rule) {
                showTournamentRule(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            }
        }
    }));
}

function completeFormAndStartRegistration(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>) {
    const tournamentEdition = registeredTournament.tournament_edition;
    tournamentService.triggerTournamentEditionRegistration({ external_id: registeredTournament.external_id, tournament_edition_id: tournamentEdition.external_id, custom_fields_answers: registeredTournament.custom_fields_answers }).then(({ sanitized }: SanitizedResponse<ITournamentEditionRegistered>) => {
        Object.assign(registeredTournament, sanitized);
        showTeamSelectionAndPaymentDetails(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
    }).catch((err) => {
        BaseService.reportError(err);
        mutableRoot.current?.render(buildMainContentView({ text: labels.you_shall_not_pass, icon: "fa fa-times err" }, err.message));
        controlMutableRoot.current?.render(buildControlsView({
            scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.retry, onClick: () => {
                showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            }
        }, {
            text: i18nManager.Labels.common.back,
            onClick: () => {
                showImportantInformation(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                if (tournamentEdition.rule) {
                    showTournamentRule(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                }
            }
        }));
    });
}

function showTeamSelectionAndPaymentDetails(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>) {
    const tournamentEdition = registeredTournament.tournament_edition;
    const profileCurrency = i18nManager.getCountryCurrency(profile.country_code);
    const tournamentRegistrationCurrency = ((tournamentEdition.registration && profileCurrency in tournamentEdition.registration) ? profileCurrency : Object.keys(tournamentEdition.registration ?? {})[0]);

    secondaryMutableRoot.current?.render(null);
    Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { rid: registeredTournament.external_id, id: tournamentEdition.external_id, state: "TEAM_SELECTION_AND_PAYMENT" });
    renderLoadingView(mutableRoot, controlMutableRoot);
    if (registeredTournament.status === RegisteredTournamentStatus.REGISTERED) {
        renderRegistrationSuccess(profile, registeredTournament, mutableRoot, controlMutableRoot);
        return;
    }
    tournamentService.getTournamentEditionRegistrationFee(registeredTournament.external_id, tournamentRegistrationCurrency).then(({ sanitized: { charge_fee: chargeFee, registration_cost: registrationCost } }: SanitizedResponse<{ charge_fee: number; registration_cost: number; }>) => {
        renderParticipantAndPaymentView(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, tournamentRegistrationCurrency, registrationCost, chargeFee);
    }).catch((err) => {
        BaseService.reportError(err);
        mutableRoot.current?.render(buildMainContentView({ text: labels.you_shall_not_pass, icon: "fa fa-times err" }, err.message));
        controlMutableRoot.current?.render(buildControlsView({
            scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.retry, onClick: () => {
                showTeamSelectionAndPaymentDetails(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            }
        }, {
            text: i18nManager.Labels.common.back,
            onClick: () => {
                showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
            }
        }));
    });
}

function renderDisabledPaymentControls(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>, paid: boolean) {
    controlMutableRoot.current?.render(buildControlsView({
        scheme: Schemes.OMNEHOST, text: (paid ? labels.proceed_to_pay : labels.proceed), disabled: true, onClick: () => { }
    }, {
        text: i18nManager.Labels.common.back,
        onClick: () => {
            secondaryMutableRoot.current?.render(undefined);
            showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
        }
    }));
}

function renderParticipantAndPaymentView(profile: IMember, registeredTournament: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>, tournamentRegistrationCurrency: string, registrationCost: number, chargeFee: number) {
    let acceptedTerms = false;
    const paid = registrationCost > 0;
    let selectedTeam: ITeam | undefined;
    const totalToPay = registrationCost + chargeFee;
    const selectedExtraTeamMembers: ITeamMember[] = [];
    const selectedTeamMembers: (ITeamMember | IMember)[] = [];
    const tournamentEdition = registeredTournament.tournament_edition;
    const currencySign = i18nManager.getCurrencySign(tournamentRegistrationCurrency);

    function renderPaymentAndParticipantsErrorView(err: any) {
        BaseService.reportError(err);
        mutableRoot.current?.render(buildMainContentView({ text: labels.registration_failed, icon: "fa fa-times err" }, err.message));
        controlMutableRoot.current?.render(buildControlsView({
            scheme: Scheme.DANGER, text: i18nManager.Labels.common.close, onClick: () => dialog?.hide()
        }, {
            text: i18nManager.Labels.common.back,
            onClick: () => {
                renderParticipantAndPaymentView(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, tournamentRegistrationCurrency, registrationCost, chargeFee);
            }
        }));
    }

    function selectTeamMember(category?: "MEMBER" | "SUB", member?: ITeamMember | IMember, team?: ITeam) {
        if (team) selectedTeam = team;
        if (member) {
            const index = (category === "MEMBER" ? selectedTeamMembers : selectedExtraTeamMembers).findIndex((m) => m.external_id === member.external_id);
            if (index > -1) {
                (category === "MEMBER" ? selectedTeamMembers : selectedExtraTeamMembers).splice(index, 1);
            } else {
                if ((category === "MEMBER" && selectedTeamMembers.length === TeamTypesValue[tournamentEdition.team_type]) || (category === "SUB" && selectedExtraTeamMembers.length === TeamTypesValue[tournamentEdition.team_type])) {
                    return true;
                }
                (category === "MEMBER" ? selectedTeamMembers : selectedExtraTeamMembers).push(member);
            }
        }
        if (acceptedTerms && ((selectedTeamMembers.length > 0 && tournamentEdition.participants_settings.allow_incomplete_team) || selectedTeamMembers.length === TeamTypesValue[tournamentEdition.team_type])) {
            controlMutableRoot.current?.render(buildControlsView({
                scheme: Schemes.OMNEHOST, text: (paid ? labels.proceed_to_pay : labels.proceed), onClick: () => {
                    secondaryMutableRoot.current?.render(undefined);
                    renderLoadingView(mutableRoot, controlMutableRoot);
                    tournamentService.addParticipantsToTournamentEditionRegistration(registeredTournament.external_id, {
                        participant_ids:  selectedTeamMembers.map((tm) => tm.external_id),
                        type: (tournamentEdition.team_type === TeamTypes.SOLO ? "MEMBER" : "TEAM"),
                        extra_participant_ids:  selectedExtraTeamMembers.map((tm) => tm.external_id),
                        resource_id: (tournamentEdition.team_type === TeamTypes.SOLO ? profile?.external_id : selectedTeam?.external_id),
                    }).then(({ }: SanitizedResponse<any>) => {
                        // TODO try charge card then present options
                        tournamentService.processTournamentEditionRegistrationPayment(registeredTournament.external_id, tournamentRegistrationCurrency).then(({ status, sanitized }: SanitizedResponse<any>) => {
                            if (status === 204) {
                                renderRegistrationSuccess(profile, registeredTournament, mutableRoot, controlMutableRoot);
                            } else if (sanitized.payment_redirect_url) {
                                window.location.href = sanitized.payment_redirect_url;
                            } else {
                                renderPaymentAndParticipantsErrorView(new Error("WTF"));
                            };
                        }).catch(renderPaymentAndParticipantsErrorView);
                    }).catch(renderPaymentAndParticipantsErrorView);
                }
            }, {
                text: i18nManager.Labels.common.back,
                onClick: () => {
                    secondaryMutableRoot.current?.render(undefined);
                    showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                }
            }));
        } else {
            renderDisabledPaymentControls(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, paid);
        }
    }

    function __render(teams: ITeam[]) {
        if (tournamentEdition.team_type === TeamTypes.SOLO) selectTeamMember("MEMBER", profile);
        mutableRoot.current?.render(<div className="tour-team-payment">
            {renderParticipantView(profile, registeredTournament, teams, mutableRoot, controlMutableRoot, secondaryMutableRoot, currencySign, registrationCost, chargeFee, selectTeamMember)}
            {!paid
                ? (<div className="tour-free">
                    <img src={CheersSvg} />
                    <span dangerouslySetInnerHTML={{ __html: labels.free_to_enter }} />
                </div>)
                : (<div className="tour-paid">
                    <span className="ps">{labels.payment.payment_summary}</span>
                    <div>
                        <span className="k">{labels.payment.registration_fee}</span>
                        <span className="v">{currencySign} {StringHelper.toPresentableMoneyValue(registrationCost)}</span>
                    </div>
                    <div>
                        <span className="k">{labels.payment.charge_fee}</span>
                        <span className="v">{currencySign} {StringHelper.toPresentableMoneyValue(chargeFee)}</span>
                    </div>
                    <div>
                        <span className="k" style={{ fontWeight: "bold" }}>{labels.payment.total_amount}</span>
                        <span className="v">{currencySign} {StringHelper.toPresentableMoneyValue(totalToPay)}</span>
                    </div>
                </div>)}
            <hr style={{ marginTop: 20 }} />
            <Checkbox scheme={Schemes.OMNEHOST} label={<span style={{ marginLeft: 10, color: "var(--textColor)" }} dangerouslySetInnerHTML={{ __html: labels.accept_tournament_terms }} />} onChecked={(checked) => {
                acceptedTerms = checked;
                selectTeamMember();
            }} />
            <hr />
        </div>);
        renderDisabledPaymentControls(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, paid);
    }

    if (tournamentEdition.team_type === TeamTypes.SOLO) {
        __render([]);
        return;
    }
    teamService.queryTeams({
        size: 100,
        status: "ACTIVE",
    }).then(({ sanitized }: SanitizedResponse<ITeam>) => {
        __render((sanitized as any).content);
    }).catch(BaseService.reportError);
}

function renderParticipantView(profile: IMember, registeredTournament: ITournamentEditionRegistered, teams: ITeam[], mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>, secondaryMutableRoot: React.RefObject<ReactDOM.Root>, tournamentRegistrationCurrency: string, registrationCost: number, chargeFee: number,
    selectTeamMember: (category: "MEMBER" | "SUB", member: ITeamMember, team?: ITeam) => void | boolean) {

    const tournamentEdition = registeredTournament.tournament_edition;
    if (tournamentEdition.team_type === TeamTypes.SOLO) {
        return (<div className="team-solo" style={{ marginBottom: 20 }}>
            <span className="info">{labels.your_account_will_be_used}</span>
            <div className="profile-brief">
                <img alt={profile.username} src={profile.profile_picture_url} />
                <div>
                    <span className="u lined-limit">{profile.username}</span>
                    <span className="pd lined-limit">{profile.tag_line}</span>
                </div>
            </div>
        </div>);
    }

    return (<div className="team-multi">
        <span className="info">{labels.select_your_team}</span>
        <Dropdown fill scheme={Schemes.OMNEHOST} options={teams} optionMap={{ icon: "logo_url", label: "name", value: "external_id" }} onSelectOption={(team: ITeam) => {
            secondaryMutableRoot.current?.render(<div style={{ width: 440, display: "flex", height: "60%" }}><img alt={"loading tournament hosts"} src={LoadingGif} style={{ width: 50, margin: "auto" }} /></div>);
            teamService.queryTeamMembers(team.external_id, { status: "ACTIVE" }).then(({ sanitized }: SanitizedResponse<ITeamMember>) => {
                secondaryMutableRoot.current?.render(<>
                    <span style={{ fontWeight: "bold", fontSize: 18, margin: 15 }}>{labels.select_x_team_member.replaceAll("${count}", "5")}</span>
                    <div style={{ margin: 20, marginTop: 20, width: 400, overflow: "auto", flex: 1 }}>
                        {(sanitized as any).content?.map((member: ITeamMember) => {

                            return (<React.Fragment key={member.external_id + "_" + DOMHelper.uniqueElementId("TM")} >
                                <Checkbox scheme={Schemes.OMNEHOST} label={<div className="profile-brief" style={{ marginLeft: 10, marginTop: 0 }}>
                                    <img alt={member.display_name} src={member.passport_picture_url} />
                                    <div>
                                        <span className="u lined-limit">{member.display_name}</span>
                                        <span className="pd lined-limit">{member.profile_details}</span>
                                    </div>
                                </div>} onChecked={() => selectTeamMember("MEMBER", member, team)} />
                                <hr />
                            </React.Fragment>);
                        })}
                    </div>
                    <span style={{ fontWeight: "bold", fontSize: 18, margin: 15 }}>{labels.select_x_sub_team_member.replaceAll("${count}", "1")}</span>
                    <div style={{ margin: 20, marginTop: 20, width: 400, overflow: "auto", flex: 0.4 }}>
                        {(sanitized as any).content?.map((member: ITeamMember) => {

                            return (<React.Fragment key={member.external_id + "_" + DOMHelper.uniqueElementId("TM")}>
                                <Checkbox scheme={Schemes.OMNEHOST} label={<div className="profile-brief" style={{ marginLeft: 10, marginTop: 0 }}>
                                    <img alt={member.display_name} src={member.passport_picture_url} />
                                    <div>
                                        <span className="u lined-limit">{member.display_name}</span>
                                        <span className="pd lined-limit">{member.profile_details}</span>
                                    </div>
                                </div>} onChecked={() => selectTeamMember("SUB", member, team)} />
                                <hr />
                            </React.Fragment>);
                        })}
                    </div>
                </>)
            }).catch((err) => {
                BaseService.reportError(err);
                secondaryMutableRoot.current?.render(undefined);
                mutableRoot.current?.render(buildMainContentView({ text: labels.you_shall_not_pass, icon: "fa fa-times err" }, err.message));
                controlMutableRoot.current?.render(buildControlsView({
                    scheme: Schemes.OMNEHOST, text: i18nManager.Labels.common.retry, onClick: () => {
                        renderParticipantAndPaymentView(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot, tournamentRegistrationCurrency, registrationCost, chargeFee);
                    }
                }, {
                    text: i18nManager.Labels.common.back,
                    onClick: () => {
                        showRequiredInformations(profile, registeredTournament, mutableRoot, controlMutableRoot, secondaryMutableRoot);
                    }
                }));
            });
        }} />
    </div>);
}

export function buildAddToCalenderView() {
    return (<>
        <span className="sh">{labels.success.save_dates}</span>
        <div className="cals" style={{ display: "flex", marginTop: 10 }}>
            <div className="cal">
                <i className="fa fa-calendar" />
                <span>{i18nManager.Labels.time_keeping.google_calender}</span>
            </div>
            <div className="cal">
                <i className="fa fa-calendar" />
                <span>{i18nManager.Labels.time_keeping.outlook_calender}</span>
            </div>
        </div>
    </>);
}

function renderRegistrationSuccess(_: IMember, __: ITournamentEditionRegistered, mutableRoot: React.RefObject<ReactDOM.Root>, controlMutableRoot: React.RefObject<ReactDOM.Root>) {
    Subscriber.report("TOURNAMENT.REGISTRATION.STATE", { clear: true });
    mutableRoot.current?.render(buildMainContentView({ text: labels.success.title, icon: "fa fa-check info" }, (<div className="tour-success">
        <span className="d">{labels.success.desc}</span>
        {Elements.buildShareToView()}
        {buildAddToCalenderView()}
    </div>)));
    controlMutableRoot.current?.render(buildControlsView({
        scheme: Schemes.OMNEHOST, text: labels.success.view_pass, onClick: () => {
            showTournamentPass(TournamentService.TEST_REGISTERED_TOURNAMENTS[0]);
        }
    }, true));
}

export function showTournamentPass(registeredTournament: ITournamentEditionRegistered) {
    const dialog = ConfirmDialog({
        noControl: true,
        style: { maxWidth: "90%" },
        contentStyle: { padding: 0 },
        content: (<div className="tournament-pass">
            <PageDesign noControl activeDesign={registeredTournament.pass_settings} leftPanelStyle={{ flex: "unset", maxHeight: 600 }} publicId={""} designType={""} onUpdate={updateDesign}
                secondaryControl={<div className="tour-diag-pass-share">
                    {Elements.buildShareToView()}
                    <span className="link" style={{ marginTop: 50 }} onClick={() => dialog?.hide()}>{i18nManager.Labels.common.close}</span>
                </div>}
                secondaryView={<div className="tour-diag-pass-cals">
                    <div>
                        {buildAddToCalenderView()}
                    </div>
                    <Button raised scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.download} style={{ margin: "20px 0px 0px 0px", padding: "10px 25px" }} />
                </div>} />
        </div>),
        onCancel: () => {
            Subscriber.report("TOURNAMENT.REGISTRATION.FETCH");
        }
    });
    dialog.show();

    function updateDesign(pageDesign: any, onDone?: () => void) {

    }
}
